import { Analytics, Upload, ViewList } from '@mui/icons-material'
import { Box, Button, Card, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import React, { useCallback, useEffect, useState } from 'react'
import TableHeader from '../../common/TableHeader'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { leadingZeroFormatter } from '../../../utils/utilities'
import CustomTableColumnLabel from '../../common/CustomTableColumnLabel'
import api from '../../../service/api'
import { exportAssignmentsAuditReport } from '../../../utils/auditing-export-utilities'
import NoDatImg from '../../../assets/no-data.svg'
import LineBarGraph from '../../common/LineBarGraph'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import CustomSwitch from '../../common/CustomSwitch'


const AssignmentsStats = () => {

    const [searchParams, setSearchParams] = useSearchParams( { sortByDept: "0", searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "0", sortBySubmissionsCount: "0", sortByAssignmentCount: "0" } )
    const month = searchParams.get( 'month' ) || ""
    const view = searchParams.get( 'view' ) || "tabular"
    const searchText = searchParams.get( 'searchText' ) || ""
    const sortByName = searchParams.get( 'sortByName' ) || "0"
    const sortByDept = searchParams.get( 'sortByDept' ) || "0"
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"
    const sortByAssignmentCount = searchParams.get( 'sortByAssignmentCount' ) || "0"
    const sortBySubmissionsCount = searchParams.get( 'sortBySubmissionsCount' ) || "0"

    const [page, setPage] = useState( 0 )
    const [loading, setLoading] = useState( true )
    const [graphData, setGraphData] = useState( [] )
    const [statsError, setStatsError] = useState( null )
    const [graphError, setGraphError] = useState( null )
    const [facultyStats, setFacultyStats] = useState( [] )
    const [showMonthPicker, setShowMonthPicker] = useState( false )
    const [loadingGraphData, setLoadingGraphData] = useState( true )
    const [last12MonthsStats, setLast12MonthsStats] = useState( null )
    const [sortedFacultyStats, setSortedFacultyStats] = useState( [] )
    const [showzeroassignmentsfaculties, setShowzeroassignmentsfaculties] = useState( false )
    const [rowsPerPage, setRowsPerPage] = useState( +( localStorage.getItem( 'adminAuditRowsPerPage' ) || 50 ) )

    const { palette, table } = useTheme()

    const navigate = useNavigate()

    const hasFilters = !( !month && selectedDepartment === "all" && selectedInstitute === "all" && !searchText )

    const setSearchParamValue = ( key, val ) => {
        const newParams = new URLSearchParams( searchParams )
        newParams.set( key, val )
        setSearchParams( newParams, { replace: true } )
    }

    const removeAppliedFilters = () => {
        const newParams = new URLSearchParams( searchParams )
        newParams.set( 'searchText', "" )
        newParams.set( 'month', "" )
        newParams.set( 'selectedDepartment', "all" )
        newParams.set( 'selectedInstitute', "all" )
        setSearchParams( newParams, { replace: true } )
    }

    const handleFilterChange = ( e, newVal ) => {
        if ( newVal === '' || newVal === 'last30days' ) {
            setSearchParamValue( 'month', newVal )
        } else {
        }
    }

    const handleMonthChange = newDate => {
        const date = new Date( newDate )
        if ( date !== 'Invalid Date' ) {
            setSearchParamValue( 'month', `${date.getFullYear()}-${leadingZeroFormatter.format( date.getMonth() + 1 )}` )
            setShowMonthPicker( false )
        }
    }

    const searchFilterFunc = item => {
        const searchValue = new RegExp( searchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' )
        if ( selectedDepartment !== "all" ) {
            const [department_id, legacy_department_id] = selectedDepartment?.split( "-" )
            const doesMatchDepartment = ( item.legacy_system_data && +legacy_department_id === +item.department_id ) || ( !item.legacy_system_data && +department_id === +item.department_id )
            return doesMatchDepartment && ( item.department?.match( searchValue ) || item.faculty_name?.match( searchValue ) || item.assignment_created_by?.match( searchValue ) )
        } else
            return item.department?.match( searchValue ) || item.faculty_name?.match( searchValue ) || item.assignment_created_by?.match( searchValue )
    }

    const columns =
        [
            { id: 1, name: 'Sl. No.', minWidth: "70px", align: "center", padding: "10px 20px 10px 40px" },
            { id: 2, name: <CustomTableColumnLabel data={sortedFacultyStats} setData={setSortedFacultyStats} label="Faculty" setSortState={setSearchParamValue} sortKey={"faculty_name"} sortName="sortByName" sortFlag={sortByName} sortValueType="string" />, minWidth: "150px", padding: "10px 20px" },
            { id: 3, name: 'AUID', padding: "10px 20px" },
            { id: 6, name: <CustomTableColumnLabel data={sortedFacultyStats} setData={setSortedFacultyStats} label="Department" setSortState={setSearchParamValue} sortKey={"department"} sortName="sortByDept" sortFlag={sortByDept} sortValueType="string" />, minWidth: "150px", padding: "10px 20px" },
            { id: 4, align: "center", style: { align: "center" }, name: <CustomTableColumnLabel align="center" data={sortedFacultyStats} setData={setSortedFacultyStats} label="Assignments" setSortState={setSearchParamValue} sortKey={"count"} sortName="sortByAssignmentCount" sortFlag={sortByAssignmentCount} sortValueType="number" />, minWidth: "150px", padding: "10px 20px" },
            { id: 5, align: "center", style: { align: "center" }, name: <CustomTableColumnLabel align="center" data={sortedFacultyStats} setData={setSortedFacultyStats} label="Submissions" setSortState={setSearchParamValue} sortKey={"submissions_received"} sortName="sortBySubmissionsCount" sortFlag={sortBySubmissionsCount} sortValueType="number" />, minWidth: "150px", padding: "10px 40px 10px 20px" },
        ]



    const getGraphData = useCallback( async () => {
        try {
            setLoadingGraphData( true )
            const institute = selectedInstitute !== "all" ? selectedInstitute : ""
            const department = selectedDepartment !== "all" ? selectedDepartment : ""
            const { data: graphDetails } = await api.audit.getAssignmentGraph( { month, institute, department } )
            setGraphData( graphDetails.data )
            setGraphError( null )
        } catch ( err ) {
            console.log( err )
            setGraphError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the assignment graph data." } )
        } finally {
            setLoadingGraphData( false )
        }
    }, [month, selectedInstitute, selectedDepartment] )


    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: { facultyStats, zeroAssignmentFaculties = [], last12MonthsStats = {} } } } = await api.audit.getAssignmentStats( { month, showzeroassignmentsfaculties, institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            let alldata = facultyStats
            if ( showzeroassignmentsfaculties ) {
                alldata = [...zeroAssignmentFaculties, ...facultyStats]
            }
            setFacultyStats( alldata )
            setSortedFacultyStats( alldata )
            setLast12MonthsStats( Object.keys( last12MonthsStats ).length > 0 ? last12MonthsStats : null )
            setStatsError( null )
        } catch ( err ) {
            console.log( err )
            setStatsError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the assignment audit data." } )
        } finally {
            setLoading( false )
        }
    }, [month, selectedInstitute, showzeroassignmentsfaculties] )

    useEffect( () => {
        getData()
    }, [getData] )

    useEffect( () => {
        getGraphData()
    }, [getGraphData] )

    return (
        <Card sx={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>
            <Box padding="20px" flexWrap="wrap" display="flex" gap="20px" justifyContent="space-between" >
                <Box>
                    <Typography color="primaryDark.main" fontSize="16px" variant='h6'>Assignment Statistics</Typography>
                    {month === "" && view === 'tabular' && <Box display="flex" alignItems="center" gap="10px">
                        <Typography color="primaryDark.main" fontSize="12px" variant='subtitle2'>Show faculty with zero assignments</Typography>
                        <CustomSwitch checked={showzeroassignmentsfaculties} onChange={e => setShowzeroassignmentsfaculties( e.target.checked )} />
                    </Box>}
                </Box>
                <Box position="relative" flexWrap="wrap" display="flex" gap="20px" alignItems="center">
                    <ToggleButtonGroup size='small' value={view} exclusive onChange={( e, newVal ) => setSearchParamValue( 'view', newVal )} >
                        <Tooltip title="Tabular view">
                            <ToggleButton value='tabular' sx={{ padding: "6px", '&.Mui-selected': { background: palette.primary.light, color: "white", '&:hover': { background: palette.primary.light } } }} size="small">
                                <ViewList fontSize='small' />
                            </ToggleButton>
                        </Tooltip>
                        <Tooltip title="Graphical view">
                            <ToggleButton sx={{ padding: "6px", '&.Mui-selected': { background: palette.primary.light, color: "white", '&:hover': { background: palette.primary.light } } }} value='graphical' size="small">
                                <Analytics fontSize='small' />
                            </ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>

                    <ToggleButtonGroup size='small' onChange={handleFilterChange} exclusive value={month || ""}>
                        {view === 'tabular' && <ToggleButton size='small' sx={{ textTransform: "capitalize", padding: "4.5px 10px" }} value="">All</ToggleButton>}
                        <ToggleButton size='small' sx={{ textTransform: "capitalize", padding: "4.5px 10px" }} value="last30days">Last 30 days</ToggleButton>
                        <ToggleButton size='small' sx={{ textTransform: "capitalize", padding: "4.5px 10px" }} value="-" selected={Boolean( month.match( /^(\d{4}-\d{2})$/ ) )}>
                            {month && month !== 'last30days' ? month : "Select month"}
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    open={showMonthPicker}
                                    onClose={() => setShowMonthPicker( false )}
                                    id="startdatetime"
                                    slotProps={{
                                        textField: {
                                            onClick: () => setShowMonthPicker( true )
                                        }
                                    }}
                                    slots={{
                                        openPickerButton: () => null,
                                    }}
                                    sx={{ opacity: 0, position: "absolute", inset: 0, fontSize: "14px", '& input': { padding: "5px 10px", cursor: "pointer" } }}
                                    // value={dayjs( endDateTime )}
                                    views={['year', 'month']}
                                    onMonthChange={handleMonthChange}
                                />
                            </LocalizationProvider>
                        </ToggleButton>
                    </ToggleButtonGroup>


                    <Button size="small" onClick={() => navigate( `list` )} variant="contained" disableElevation sx={{ height: "33.5px", textTransform: "capitalize", background: "#FFE0E0", border: `1px solid #FF6C8F`, color: "#FF736F", '&:hover': { background: "#FFE0E0", color: "#FF736F" } }} >View Assignments</Button>
                    <Button size="small" onClick={() => exportAssignmentsAuditReport( sortedFacultyStats, last12MonthsStats, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-assignments-audit-report` )} variant="contained" disableElevation sx={{ height: "33.5px", background: `linear-gradient(200.17deg, #1CB5B5 -47.65%, #105958 167.67%)`, color: "#fff", '&:hover': { background: `linear-gradient(200.17deg, #1CB5B5 -47.65%, #105958 167.67%)`, color: "#fff" } }} endIcon={<Upload fontSize='small' />} >Export</Button>
                </Box>
            </Box >
            {!statsError && !loading && view === 'tabular' && sortedFacultyStats.filter( searchFilterFunc ).length > 0 && <Box overflow="hidden" bgcolor={palette.contentBg} borderRadius="10px" margin="0 20px 20px 20px">
                <TableContainer sx={{ overflow: "auto", maxHeight: "500px" }} className='custom-scrollbar' >
                    <Table stickyHeader>
                        <TableHeader columns={columns} />
                        <TableBody>
                            {sortedFacultyStats.filter( searchFilterFunc ).slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage ).map( ( row, index ) => {
                                return <TableRow sx={{ background: +row.count === 0 ? palette.error.light + "44" : "" }} className='table-row' key={row.assignment_created_by + index}>
                                    <TableCell align={columns[0].align || "left"} sx={{ fontSize: "12px", padding: columns[0].padding || "10px 20px", color: table.color }}><strong>{index + 1}</strong></TableCell>
                                    <TableCell sx={{ fontSize: "12px", padding: columns[1].padding || "10px 20px", color: table.color, textTransform: "capitalize", '&:hover': { textDecoration: "underline" } }}><strong>{row.faculty_name?.toLowerCase()}</strong></TableCell>
                                    <TableCell align={columns[2].align || "left"} sx={{ fontSize: "12px", padding: columns[2].padding || "10px 20px", color: table.color, '&:hover': { textDecoration: "underline" } }}><strong>{row.assignment_created_by}</strong></TableCell>
                                    <TableCell align={columns[3].align || "left"} sx={{ fontSize: "12px", padding: columns[3].padding || "10px 20px", color: table.color, textTransform: "capitalize" }}><strong> <small>{row.institute} - {row.department?.toUpperCase()}</small></strong></TableCell>
                                    <TableCell align={columns[4].align || "left"} sx={{ fontSize: "12px", padding: columns[3].padding || "10px 20px", color: table.color }}><strong>{row.count}</strong></TableCell>
                                    <TableCell align={columns[5].align || "left"} sx={{ fontSize: "12px", padding: columns[4].padding || "10px 20px", color: table.color }}><strong>{row.submissions_received}</strong></TableCell>
                                </TableRow>
                            } )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    sx={{ background: table.headerBg, position: "sticky", bottom: 0, flexShrink: 0 }}
                    rowsPerPageOptions={[50, 100, 150, 200]}
                    component="div"
                    count={sortedFacultyStats.filter( searchFilterFunc ).length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={( event, val ) => setPage( val )}
                    onRowsPerPageChange={( event ) => {
                        setRowsPerPage( parseInt( event.target.value, 10 ) )
                        localStorage.setItem( 'adminAuditRowsPerPage', event.target.value )
                        setPage( 0 )
                    }}
                />
            </Box>}
            {statsError && <TryAgainMessageBlock err={statsError.message} loading={loading} getData={getData} code={statsError.code} />}
            {!statsError && !loading && sortedFacultyStats.filter( searchFilterFunc ).length === 0 && <Box flexDirection="column" padding="20px" display="flex" alignItems="center" justifyContent="center" gap="20px">
                <img src={NoDatImg} height="120px" alt="No assignmets in last 30 days" />
                <Typography textAlign="center" variant='subtitle2'>No assignments were found for applied filters!</Typography>
                {( facultyStats.length > 0 || hasFilters ) && <Button onClick={removeAppliedFilters} sx={{ textTransform: "capitalize" }} color="secondary" variant="contained" disableElevation>Remove filters</Button>}
            </Box>}
            {!loading && !loadingGraphData && view !== 'tabular' && sortedFacultyStats.filter( searchFilterFunc ).length > 0 &&
                <Box display="flex" flexGrow={1} minHeight="400px">
                    {!graphError ? <LineBarGraph graphTitle="" barLegend='Submissions submitted' yAxisName="Assignments" xAxisName="Date" y2AxisName="Submissions" lineLegend='Assignments created' barValuesKey="submissions" barData={graphData} dateformat={"%Y-%m-%d"} xAxisValuesKey={'date'} lineValuesKey={'assignments_created'} lineData={graphData} />
                        : <TryAgainMessageBlock err={graphError.message} loading={loadingGraphData} getData={getGraphData} code={graphError.code} />}
                </Box>
            }
            {( loading || loadingGraphData ) && !statsError && !graphError && <DataLoadingSpinner waitingMessage="Loading assignment audit report please wait..." />}
        </Card >
    )
}

export default AssignmentsStats
